/**
 * Per-exercise load-increment helper.
 *
 * Why this exists
 * ---------------
 * The original APRE/DUP autoregulation used a single hardcoded constant
 * (2.5 kg / 5 lb) for BOTH the working-weight rounding AND the Mann
 * adjustment table. That works for barbells (smallest plate pair = 1.25 kg
 * × 2 = 2.5 kg) but is wildly too coarse for:
 *   • Dumbbells — most gyms have 1 kg or 2 kg jumps; a +2.5 kg jump on a
 *     7.5 kg lateral raise is +33% load in one week.
 *   • Cable / pin machines — usually 5–10 lb pin increments.
 *   • Micro-plate users — 0.25 / 0.5 kg jumps for accessory lifts.
 *
 * Resolution order for an exercise's effective increment:
 *   1. `exercise.loadIncrement` (explicit user override, set in the
 *      APRE/DUP setup dialog).
 *   2. `defaultIncrementForEquipment(exercise.equipment, unit)` — sensible
 *      default per equipment type.
 *   3. Final fallback: 2.5 kg / 5 lb.
 *
 * The increment is shared between APRE-10 and DUP-3 — a dumbbell press is
 * a dumbbell press regardless of which autoregulation algorithm is on.
 */

export type Unit = 'kg' | 'lb';

/** Sensible default increment per equipment type. Updated Feb 2026 after
 *  user feedback: most gyms carry 0.5 / 1 / 1.25 kg micro plates that
 *  attach to dumbbells AND cable handles, so machines/cables shouldn't
 *  default to the same coarse 2.5 kg as a barbell. */
export function defaultIncrementForEquipment(
  equipment: string | undefined | null,
  unit: Unit = 'kg',
): number {
  const eq = (equipment || '').toLowerCase().trim();
  if (unit === 'lb') {
    if (eq.includes('dumbbell')) return 1;
    if (eq === 'bodyweight') return 1;
    if (eq.includes('cable') || eq.includes('machine') || eq.includes('pin')) return 2.5;
    // barbell, ez bar, smith machine, trap bar, plate(s), default
    return 5;
  }
  // kg
  if (eq.includes('dumbbell')) return 0.5;
  if (eq === 'bodyweight') return 1;
  if (eq.includes('cable') || eq.includes('machine') || eq.includes('pin')) return 1.25;
  // barbell, ez bar, smith machine, trap bar, plate(s), default
  return 2.5;
}

/** Resolve the effective increment for an exercise. */
export function getExerciseIncrement(exercise: any, unit: Unit = 'kg'): number {
  const override = Number(exercise?.loadIncrement);
  if (override > 0) return override;
  return defaultIncrementForEquipment(exercise?.equipment, unit);
}

/** Round to the nearest loadable weight given a specific increment. */
export function roundToIncrement(weight: number, inc: number): number {
  if (!inc || inc <= 0) return weight;
  return Math.max(inc, Math.round(weight / inc) * inc);
}

/** Selectable increment choices shown in the APRE/DUP setup dialog. */
export const INCREMENT_CHOICES_KG: number[] = [0.25, 0.5, 1, 1.25, 2.5, 5];
export const INCREMENT_CHOICES_LB: number[] = [0.5, 1, 2.5, 5, 10];

export function getIncrementChoices(unit: Unit = 'kg'): number[] {
  return unit === 'lb' ? INCREMENT_CHOICES_LB : INCREMENT_CHOICES_KG;
}
